
import React, { useState, useEffect } from 'react';
import { supabase, tables } from '@/lib/supabase';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { StripeConnectRequest } from '@/services/payment/types';
import { StandardPageHeader } from '@/components/layout/StandardPageHeader';
import { PageContainer } from '@/components/layout/PageContainer';
import { useAuth } from '@/hooks/useAuth';
import { format } from 'date-fns';

const StripeConnectRequests = () => {
  const { user, userRole } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [requests, setRequests] = useState<StripeConnectRequest[]>([]);
  const [isRejectDialogOpen, setIsRejectDialogOpen] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [currentRequestId, setCurrentRequestId] = useState<string | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  
  const isSuperAdmin = userRole === 'superadmin';
  
  useEffect(() => {
    if (!isSuperAdmin) {
      toast.error("You don't have permission to access this page");
      return;
    }
    
    fetchRequests();
  }, [isSuperAdmin]);
  
  const fetchRequests = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('stripe_connect_requests' as any)
        .select('*')
        .order('requested_at', { ascending: false });

      if (error) {
        throw error;
      }

      // Type assertion to ensure the data is treated as the expected type
      setRequests(data as unknown as StripeConnectRequest[]);
    } catch (error) {
      console.error('Error fetching Stripe Connect requests:', error);
      toast.error('Failed to load Stripe Connect requests');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleApprove = async (requestId: string) => {
    setIsApproving(true);
    setCurrentRequestId(requestId);
    
    try {
      const { data, error } = await supabase.functions.invoke("approve-stripe-connect-request", {
        body: { requestId }
      });
      
      if (error) throw error;
      if (data.error) throw new Error(data.error);
      
      toast.success("Request approved successfully");
      fetchRequests();
    } catch (error) {
      console.error("Error approving request:", error);
      toast.error("Failed to approve request");
    } finally {
      setIsApproving(false);
      setCurrentRequestId(null);
    }
  };
  
  const openRejectDialog = (requestId: string) => {
    setCurrentRequestId(requestId);
    setRejectionReason('');
    setIsRejectDialogOpen(true);
  };
  
  const handleReject = async () => {
    if (!currentRequestId) return;
    
    setIsRejecting(true);
    
    try {
      const { data, error } = await supabase.functions.invoke("reject-stripe-connect-request", {
        body: { 
          requestId: currentRequestId,
          reason: rejectionReason
        }
      });
      
      if (error) throw error;
      if (data.error) throw new Error(data.error);
      
      toast.success("Request rejected successfully");
      setIsRejectDialogOpen(false);
      fetchRequests();
    } catch (error) {
      console.error("Error rejecting request:", error);
      toast.error("Failed to reject request");
    } finally {
      setIsRejecting(false);
    }
  };
  
  if (!isSuperAdmin) {
    return (
      <div className="p-6">
        <h1 className="text-xl font-bold">Unauthorized</h1>
        <p className="mt-2">You don't have permission to access this page.</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      <StandardPageHeader
        title="Stripe Connect Requests"
        subtitle="Manage Stripe Connect account requests from organizations"
        icon={CheckCircle2}
      />
      
      <PageContainer>

      {isLoading ? (
        <div className="flex justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : requests.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-6">
              <p className="text-muted-foreground">No Stripe Connect requests found</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {requests.map((request) => (
            <Card key={request.id} className={request.status === 'pending' ? 'border-blue-200 bg-blue-50' : ''}>
              <CardHeader>
                <CardTitle className="flex justify-between items-center">
                  <span>{request.organization_name}</span>
                  <div className="flex items-center">
                    {request.status === 'pending' && (
                      <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium">
                        Pending
                      </span>
                    )}
                    {request.status === 'approved' && (
                      <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">
                        Approved
                      </span>
                    )}
                    {request.status === 'rejected' && (
                      <span className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs font-medium">
                        Rejected
                      </span>
                    )}
                  </div>
                </CardTitle>
                <CardDescription>
                  Requested by: {request.email} on {format(new Date(request.requested_at), 'MMM d, yyyy h:mm a')}
                </CardDescription>
              </CardHeader>
              
              <CardContent>
                <div className="space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <span className="text-sm font-medium">Organization ID:</span>
                      <div className="text-sm text-muted-foreground">{request.organization_id}</div>
                    </div>
                    <div>
                      <span className="text-sm font-medium">Request ID:</span>
                      <div className="text-sm text-muted-foreground">{request.id}</div>
                    </div>
                  </div>
                  
                  {request.status === 'approved' && request.approved_at && (
                    <div>
                      <span className="text-sm font-medium">Approved:</span>
                      <div className="text-sm text-muted-foreground">
                        {format(new Date(request.approved_at), 'MMM d, yyyy h:mm a')}
                      </div>
                      {request.stripe_account_id && (
                        <div className="mt-1">
                          <span className="text-sm font-medium">Stripe Account ID:</span>
                          <div className="text-sm text-muted-foreground">{request.stripe_account_id}</div>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {request.status === 'rejected' && request.rejected_at && (
                    <div>
                      <span className="text-sm font-medium">Rejected:</span>
                      <div className="text-sm text-muted-foreground">
                        {format(new Date(request.rejected_at), 'MMM d, yyyy h:mm a')}
                      </div>
                      {request.rejection_reason && (
                        <div className="mt-1">
                          <span className="text-sm font-medium">Reason:</span>
                          <div className="text-sm text-muted-foreground">{request.rejection_reason}</div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
              
              {request.status === 'pending' && (
                <CardFooter className="flex justify-end gap-2">
                  <Button 
                    variant="outline" 
                    onClick={() => openRejectDialog(request.id)}
                    disabled={isRejecting || isApproving}
                  >
                    {isRejecting && currentRequestId === request.id && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    <XCircle className="mr-2 h-4 w-4 text-red-500" />
                    Reject
                  </Button>
                  <Button 
                    onClick={() => handleApprove(request.id)}
                    disabled={isApproving || isRejecting}
                  >
                    {isApproving && currentRequestId === request.id && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    <CheckCircle2 className="mr-2 h-4 w-4" />
                    Approve
                  </Button>
                </CardFooter>
              )}
            </Card>
          ))}
        </div>
      )}
      
      <Dialog open={isRejectDialogOpen} onOpenChange={setIsRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Stripe Connect Request</DialogTitle>
            <DialogDescription>
              Please provide a reason for rejecting this request. This will be shared with the organization.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Label htmlFor="reason">Rejection Reason</Label>
            <Textarea
              id="reason"
              placeholder="Enter reason for rejection..."
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              className="mt-2"
              rows={4}
            />
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setIsRejectDialogOpen(false)}
              disabled={isRejecting}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleReject}
              disabled={isRejecting}
            >
              {isRejecting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Reject Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </PageContainer>
    </div>
  );
};

export default StripeConnectRequests;
